import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {AccountLink} from 'src/components/links';
import {RequiredMark} from 'src/components/required-mark';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {UserStatusSuggest} from 'src/components/user-status-suggest';
import {UserTypeSuggest} from 'src/components/user-type-suggest';
import {NO_ACCESS_USER_TYPE_CODE} from 'src/components/user-type-suggest/user-type-suggest';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ImportSettings} from 'src/modules/profile/account/create-user/steps/users-settings/components/imports-settings';
import {ReplaceConfirmMessage, isDisabled} from 'src/modules/profile/user/components/replace-search-settings';
import {getAccountName} from 'src/openapi/admin/apis/GetAccountNameApi';
import {getAccountTemplateUsers} from 'src/openapi/admin/apis/GetAccountTemplateUsersApi';
import {getUserNewsletter} from 'src/openapi/admin/apis/GetUserNewsletterApi';
import {UserStatus as UserStatuses} from 'src/openapi/admin/models/UserStatus';
import {UserType as UserTypes} from 'src/openapi/admin/models/UserType';
import {useAppDispatch} from 'src/store/hooks';

import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {
    selectBulkUserImportImportSettings,
    selectBulkUserImportSelectedStatus,
    selectBulkUserImportSelectedType,
    selectBulkUserImportTargetAccount,
    setImportSettings,
    setTargetAccount, setTemplateNewsletter,
    setUserStatus,
    setUserType,
} from '../../services/user-bulk-import.slice';

import {UploadBulkUserImportFileBlock} from './user-import-file-upload/upload-bulk-user-import-file';

import type {UserStatus} from 'src/openapi/admin/models/UserStatus';
import type {UserType} from 'src/openapi/admin/models/UserType';

type Props = {
    accountId: number,
    availableStatuses: string[],
};

export const UploadFileStep: React.FC<Props> = ({accountId, availableStatuses}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const targetAccount = useSelector(selectBulkUserImportTargetAccount);
    const selectedType = useSelector(selectBulkUserImportSelectedType);
    const selectedStatus = useSelector(selectBulkUserImportSelectedStatus);
    const importSettings = useSelector(selectBulkUserImportImportSettings);
    const [uploadFileError, setUploadFileError] = useState<string>('');
    const {userFieldChange} = useEventTracking();

    useEffect(() => {
        if (!availableStatuses.includes(selectedStatus)) {
            dispatch(setUserStatus(availableStatuses[0] as UserStatus));
        }
    }, [selectedStatus, availableStatuses, dispatch]);

    useEffect(() => {
        const getTemplateUserNewsletter = async () => {
            const templateUserResponse = await apiDispatch(getAccountTemplateUsers({accountId}));
            if (templateUserResponse.payload.success) {
                const templateUserNewsletter = await apiDispatch(getUserNewsletter({
                    userId: templateUserResponse.payload.data[0].userId,
                }));
                if (templateUserNewsletter.payload.success) {
                    dispatch(setTemplateNewsletter(templateUserNewsletter.payload.data));
                }
            }

        };

        if (accountId) {
            apiDispatch(getAccountName({requestBody: [accountId]})).then(response => {
                if (response.payload.success) {
                    dispatch(setTargetAccount({
                        accountId,
                        accountName: response.payload.data[accountId],
                    }));
                }
            });
            getTemplateUserNewsletter();
        }
    }, [accountId, apiDispatch, dispatch]);

    const onUserTypeChange = useCallback((typeCode: string) => {
        if (typeCode === NO_ACCESS_USER_TYPE_CODE && selectedStatus !== UserStatuses.EMPTY) {
            dispatch(setUserStatus(UserStatuses.EMPTY));
        } else if (
            typeCode !== NO_ACCESS_USER_TYPE_CODE &&
            selectedStatus === UserStatuses.EMPTY &&
            availableStatuses
        ) {
            dispatch(setUserStatus(availableStatuses[0] as UserStatus));
        }
        dispatch(setUserType(typeCode as UserType));
        userFieldChange({
            section: 'Upload File',
            parameterName: 'User type',
            viewType: 'Bulk Import Users',
        });
    }, [availableStatuses, dispatch, selectedStatus, userFieldChange]);

    const onUserStatusChange = useCallback((statusCode: string) => {
        if (statusCode === UserStatuses.EMPTY && selectedType !== UserTypes.T_NA) {
            dispatch(setUserType(UserTypes.T_NA));
        } else if (statusCode !== UserStatuses.EMPTY && selectedType === UserTypes.T_NA) {
            dispatch(setUserType(UserTypes.T_LC));
        }
        dispatch(setUserStatus(statusCode as UserStatus));
        userFieldChange({
            section: 'Upload File',
            parameterName: 'User status',
            viewType: 'Bulk Import Users',
        });
    }, [dispatch, userFieldChange, selectedType]);

    const setTemplates = useCallback((data: boolean) => {
        dispatch(setImportSettings({
            ...importSettings,
            replaceLayouts: data,
        }));
    }, [importSettings, dispatch]);

    const setEmailAlerts = useCallback((data: boolean) => {
        dispatch(setImportSettings({
            ...importSettings,
            replaceAlerts: data,
        }));
    }, [importSettings, dispatch]);

    const setSavedSearches = useCallback((data: boolean) => {
        dispatch(setImportSettings({
            ...importSettings,
            replaceSearches: data,
        }));
    }, [importSettings, dispatch]);

    const setSelectedTemplateUserId = useCallback((id: number | null) => {
        dispatch(setImportSettings({
            ...importSettings,
            templateUserId: id,
        }));
    }, [importSettings, dispatch]);

    return (
        <div className="pd-l">
            <Group direction="v" gap="xxl" alignItems="start">
                {targetAccount ? (
                    <div>
                        <Label><RequiredMark label="Account"/></Label>
                        <AccountLink
                            accountId={targetAccount.accountId}
                            accountName={targetAccount.accountName}
                            place="Bulk User Creation"
                            blank={true}
                        />
                    </div>
                ) : null}
                <UploadBulkUserImportFileBlock {...{uploadFileError, setUploadFileError}}/>
                <div style={{width: 700}}>
                    <ImportSettings
                        key="ImportOptions"
                        {...{
                            selectedTemplateUserId: importSettings.templateUserId || null,
                            savedSearches: importSettings.replaceSearches,
                            emailAlerts: importSettings.replaceAlerts,
                            templates: importSettings.replaceLayouts,
                            setSelectedTemplateUserId,
                            setEmailAlerts,
                            setTemplates,
                            setSavedSearches,
                            onFieldChange: () => {
                                userFieldChange({
                                    section: 'Upload File',
                                    parameterName: 'Import options',
                                    viewType: 'Bulk Import Users',
                                });
                            },
                        }}
                    />
                    <ReplaceConfirmMessage {...{
                        disabled: isDisabled(importSettings),
                        templates: importSettings.replaceLayouts,
                        emailAlerts: importSettings.replaceAlerts,
                        savedSearches: importSettings.replaceSearches,
                        buttonName: 'Import',
                    }}
                    />
                </div>

                <FormSection headline="Access Type">
                    <div style={{width: 700}}>
                        <ResponsiveElementGroup>
                            <div style={{width: 300}}>
                                <Label>Type</Label>
                                <UserTypeSuggest
                                    userType={selectedType}
                                    setUserType={onUserTypeChange}
                                />
                            </div>
                            {availableStatuses ? (
                                <div style={{width: 300}}>
                                    <Label>Status</Label>
                                    <UserStatusSuggest
                                        accountId={+accountId}
                                        userStatus={selectedStatus as string}
                                        availableStatuses={availableStatuses}
                                        setUserStatus={onUserStatusChange}
                                    />
                                </div>
                            ) : null}
                        </ResponsiveElementGroup>
                    </div>
                </FormSection>
            </Group>
        </div>
    );
};
